import { useState } from 'react';
import { Typography, TextField, Grid, IconButton, Button, Select, MenuItem, ListSubheader, Box, Paper, Checkbox } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import charactersticsData from '../data/characteristics.json'
import SymbolField from '../Util/SymbolField'
import speciesWeaponMode, { SPECIES_WEAPON_MODES } from '../Util/SpeciesWeaponMode'
import { qualityModeOf } from '../Util/QualityGrants'

const DIE_FIELDS = [
    { key: "SuccessCount", label: "Success" },
    { key: "AdvantageCount", label: "Advantage" },
    { key: "ThreatCount", label: "Threat" },
    { key: "BoostCount", label: "Boost" },
    { key: "AddSetbackCount", label: "Setback" },
    { key: "SetbackCount", label: "Remove Setback" },
    { key: "UpgradeAbilityCount", label: "Upgrade" },
    { key: "ForceCount", label: "Force (=FR)" },
];

const targetValue = (dieMod) =>
    dieMod.SkillType ? `TYPE::${dieMod.SkillType}`
        : dieMod.SkillChar && !dieMod.SkillKey ? `CHAR::${dieMod.SkillChar}`
            : `SKILL::${dieMod.SkillKey || ''}`;

const applyTarget = (dieMod, value) => {
    const [type, key] = value.split("::");
    const next = { ...dieMod };
    delete next.SkillKey;
    delete next.SkillChar;
    delete next.SkillType;
    if (type === "CHAR") { next.SkillChar = key; }
    else if (type === "TYPE") { next.SkillType = key; }
    else { next.SkillKey = key; }
    return next;
};

export const weaponQualityOptions = (qualities, currentKey) => qualities
    .filter(q => q.IsQuality === "true" || (currentKey && q.Key === currentKey))
    .sort((a, b) => (a.Name || "").localeCompare(b.Name || ""))

export const CategoriesField = ({ categories, resetKey, onCommit, sx }) => {
    const fromValue = () => (Array.isArray(categories) ? categories.join(", ") : (categories || ""));
    const [text, setText] = useState(fromValue());
    const [syncedKey, setSyncedKey] = useState(resetKey);
    if (resetKey !== syncedKey) {
        setSyncedKey(resetKey);
        setText(fromValue());
    }
    const commit = () => {
        const parts = text.split(",").map((s) => s.trim()).filter(Boolean);
        const next = parts.length > 1 ? parts : (parts[0] || "");
        onCommit(next);
        setText(Array.isArray(next) ? next.join(", ") : (next || ""));
    };
    return (
        <TextField
            fullWidth
            sx={sx}
            label="Categories (comma-separated)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
        />
    );
};

export const RollModEditor = ({ dieModifiers: rawDieModifiers = [], onChange, skills }) => {
    const dieModifiers = Array.isArray(rawDieModifiers) ? rawDieModifiers : rawDieModifiers ? [rawDieModifiers] : [];
    const sortedSkills = [...skills].sort((a, b) => (a.skill || "").localeCompare(b.skill || ""));
    const skillTypes = [...new Set(skills.map((s) => s.type).filter(Boolean))];

    const updateRow = (dI, newRow) => {
        const next = [...dieModifiers];
        next[dI] = newRow;
        onChange(next);
    };

    return (
        <Box sx={{ marginTop: "5px" }}>
            {dieModifiers.map((dieMod, dI) => (
                <Box key={dI} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", marginBottom: "5px" }}>
                    <Select
                        value={targetValue(dieMod)}
                        onChange={(e) => updateRow(dI, applyTarget(dieMod, e.target.value))}
                        sx={{ minWidth: "180px" }}
                    >
                        <ListSubheader>By Characteristic</ListSubheader>
                        {charactersticsData.map((c) => (
                            <MenuItem key={c.Key} value={`CHAR::${c.Key}`}>{`All ${c.Name} Skills`}</MenuItem>
                        ))}
                        <ListSubheader>By Category</ListSubheader>
                        {skillTypes.map((tp) => (
                            <MenuItem key={tp} value={`TYPE::${tp}`}>{`All ${tp} Skills`}</MenuItem>
                        ))}
                        <ListSubheader>Skill</ListSubheader>
                        {sortedSkills.map((skill) => (
                            <MenuItem key={skill.Key} value={`SKILL::${skill.Key}`}>{skill.skill}</MenuItem>
                        ))}
                    </Select>
                    {dieMod.SkillKey &&
                        <Select
                            value={dieMod.SkillChar || ""}
                            displayEmpty
                            onChange={(e) => {
                                const { SkillChar, ...rest } = dieMod;
                                updateRow(dI, e.target.value ? { ...rest, SkillChar: e.target.value } : rest);
                            }}
                            sx={{ minWidth: "160px", marginLeft: 1 }}
                        >
                            <MenuItem value="">Any characteristic</MenuItem>
                            {charactersticsData.map((c) => (
                                <MenuItem key={c.Key} value={c.Key}>{`Using ${c.Name}`}</MenuItem>
                            ))}
                        </Select>
                    }
                    {DIE_FIELDS.map((field) => (
                        <TextField
                            key={field.key}
                            label={field.label}
                            value={dieMod[field.key] || 0}
                            onChange={(e) => updateRow(dI, { ...dieMod, [field.key]: e.target.value })}
                            sx={{ maxWidth: "110px", marginLeft: 1 }}
                        />
                    ))}
                    <IconButton
                        onClick={() => onChange(dieModifiers.filter((_, i) => i !== dI))}
                        sx={{ maxWidth: "30px" }}
                        size="small"
                        className="no-print"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            ))}
            <Button
                onClick={() => onChange([...dieModifiers, { SkillKey: sortedSkills[0]?.Key || "" }])}
                className="no-print"
            >
                Add Roll Modifier
            </Button>
        </Box>
    );
};

const RANGE_BANDS = ["Engaged", "Short", "Medium", "Long", "Extreme"]
const isQualityAdjust = (key) => /^(REM|SUB)QUAL/.test(key || "")
const isRatedQuality = (qualities, key) => qualities.some(q => q.Key === key && q.IsQuality === "true" && q.IsBoolean !== "true")

export const MiscModsEditor = ({ mods = [], onChange, skills, talents, qualities, filteredQualities, integratedTarget }) => {
    const update = (index, newMod) => {
        const next = [...mods];
        next[index] = newMod;
        onChange(next);
    };
    const remove = (index) => onChange(mods.filter((_, i) => i !== index));

    const sortedSkills = [...skills].sort((a, b) => (a.skill || "").localeCompare(b.skill || ""));
    const sortedTalents = [...talents].sort((a, b) => a.Name.localeCompare(b.Name));
    const sortedQualities = [...filteredQualities].filter(q => !isQualityAdjust(q.Key)).sort((a, b) => a.Name.localeCompare(b.Name));
    const integratedQualities = qualities.filter(q => (!q.Type || q.Type === "Weapon") && !isQualityAdjust(q.Key)).sort((a, b) => a.Name.localeCompare(b.Name));
    const qualityChoices = (mod) => mod.ToIntegrated ? integratedQualities : sortedQualities;
    const hasRangeReduction = mods.some(m => m.Key === "RANGESUB");
    const adjustTargets = weaponQualityOptions(qualities.filter(q => !isQualityAdjust(q.Key)));
    const isKnownKey = (key) => skills.some(s => s.Key === key) || talents.some(t => t.Key === key)
        || charactersticsData.some(c => c.Key === key) || qualities.some(q => q.Key === key) || isQualityAdjust(key);

    return (
        <>
            {mods.length > 0 && (
                <Grid container>
                    {mods.map((mod, index) => (
                        <Grid item key={index} sx={{ margin: "7.5px" }}>
                            {(mod.MiscDesc || mod.MiscDesc === "") &&
                                <>
                                    <Typography>Custom Mod:</Typography>
                                    <SymbolField
                                        label="Description"
                                        sx={{ minWidth: "500px" }}
                                        value={mod.MiscDesc}
                                        onChange={(e) => update(index, { ...mod, MiscDesc: e.target.value })}
                                    />
                                    <TextField
                                        label="Ranks"
                                        value={mod.Count || 1}
                                        onChange={(e) => update(index, { ...mod, Count: !isNaN(e.target.value) && e.target.value !== "" ? e.target.value : mod.Count || "1" })}
                                        sx={{ maxWidth: "90px", marginLeft: "4px" }}
                                    />
                                </>
                            }
                            {mod.Key && skills.find(s => s.Key === mod.Key) &&
                                <>
                                    <Typography>Skill:</Typography>
                                    <Select value={mod.Key || ''} onChange={(e) => update(index, { ...mod, Key: e.target.value })}>
                                        {sortedSkills.map((skill) => (
                                            <MenuItem key={skill.Key} value={skill.Key}>{skill.skill}</MenuItem>
                                        ))}
                                    </Select>
                                    <TextField
                                        label="Ranks"
                                        value={mod.Count || 0}
                                        onChange={(e) => update(index, { ...mod, Count: !isNaN(e.target.value) ? e.target.value : mod.Count || 0 })}
                                        sx={{ maxWidth: "90px" }}
                                    />
                                    {" "} Make Career:
                                    <Checkbox
                                        checked={!!mod.SkillIsCareer}
                                        onChange={(e) => update(index, { ...mod, SkillIsCareer: e.target.checked })}
                                    />
                                </>
                            }
                            {mod.DieModifiers &&
                                <RollModEditor
                                    dieModifiers={mod.DieModifiers}
                                    skills={skills}
                                    onChange={(newDieMods) => update(index, { ...mod, DieModifiers: newDieMods })}
                                />
                            }
                            {mod.Key && talents.find(t => t.Key === mod.Key) &&
                                <>
                                    <Typography>Innate Talent:</Typography>
                                    <Select value={mod.Key || ''} onChange={(e) => update(index, { ...mod, Key: e.target.value })}>
                                        {sortedTalents.map((talent) => (
                                            <MenuItem key={talent.Key} value={talent.Key}>{talent.Name}</MenuItem>
                                        ))}
                                    </Select>
                                    <TextField
                                        label="Ranks"
                                        value={mod.Count || 0}
                                        onChange={(e) => update(index, { ...mod, Count: !isNaN(e.target.value) ? e.target.value : mod.Count || 0 })}
                                        sx={{ maxWidth: "90px" }}
                                    />
                                </>
                            }
                            {mod.Key && charactersticsData.find(t => t.Key === mod.Key) &&
                                <>
                                    <Typography>Characteristic Mod:</Typography>
                                    <Select value={mod.Key || ''} onChange={(e) => update(index, { ...mod, Key: e.target.value })} sx={{ maxWidth: "180px" }}>
                                        {charactersticsData.map((c) => (
                                            <MenuItem key={c.Key} value={c.Key}>{c.Name}</MenuItem>
                                        ))}
                                    </Select>
                                    <TextField
                                        label="Ranks"
                                        value={mod.Count || 0}
                                        onChange={(e) => update(index, { ...mod, Count: !isNaN(e.target.value) ? e.target.value : mod.Count || 0 })}
                                        sx={{ maxWidth: "90px" }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <span>Doesn't affect Soak/Wound Threshold:</span>
                                        <Checkbox
                                            checked={!!mod.NoSoakWound}
                                            onChange={(e) => update(index, { ...mod, NoSoakWound: e.target.checked || undefined })}
                                        />
                                    </Box>
                                </>
                            }
                            {isQualityAdjust(mod.Key) &&
                                <>
                                    <Typography>Remove/Reduce Quality:</Typography>
                                    <Select value={mod.Key.slice(0, 7)} onChange={(e) => update(index, { ...mod, Key: e.target.value + mod.Key.slice(7) })}>
                                        <MenuItem value="REMQUAL">Remove</MenuItem>
                                        <MenuItem value="SUBQUAL">Reduce</MenuItem>
                                    </Select>
                                    <Select value={mod.Key.slice(7)} onChange={(e) => update(index, { ...mod, Key: mod.Key.slice(0, 7) + e.target.value })} sx={{ minWidth: "160px" }}>
                                        {!adjustTargets.some(q => q.Key === mod.Key.slice(7)) &&
                                            <MenuItem value={mod.Key.slice(7)}>{mod.Key.slice(7)}</MenuItem>
                                        }
                                        {adjustTargets.map((q) => (
                                            <MenuItem key={q.Key} value={q.Key}>{q.Name}</MenuItem>
                                        ))}
                                    </Select>
                                    {mod.Key.startsWith("SUBQUAL") &&
                                        <TextField
                                            label="Reduce By"
                                            value={mod.Count || 1}
                                            onChange={(e) => update(index, { ...mod, Count: !isNaN(e.target.value) ? e.target.value : mod.Count || 1 })}
                                            sx={{ maxWidth: "90px" }}
                                        />
                                    }
                                </>
                            }
                            {mod.Key && !isKnownKey(mod.Key) &&
                                <>
                                    <Typography>Unrecognized Mod Key:</Typography>
                                    <TextField
                                        label="Key"
                                        value={mod.Key}
                                        onChange={(e) => update(index, { ...mod, Key: e.target.value })}
                                        sx={{ maxWidth: "200px" }}
                                    />
                                    <TextField
                                        label="Count"
                                        value={mod.Count || ''}
                                        onChange={(e) => update(index, { ...mod, Count: e.target.value })}
                                        sx={{ maxWidth: "90px" }}
                                    />
                                </>
                            }
                            {mod.Key && qualities.find(t => t.Key === mod.Key) && !isQualityAdjust(mod.Key) &&
                                <>
                                    <Typography>Quality:</Typography>
                                    <Select value={mod.Key || ''} onChange={(e) => update(index, { ...mod, Key: e.target.value })}>
                                        {!qualityChoices(mod).some(q => q.Key === mod.Key) &&
                                            <MenuItem value={mod.Key}>{qualities.find(q => q.Key === mod.Key)?.Name || mod.Key}</MenuItem>
                                        }
                                        {qualityChoices(mod).map((quality) => (
                                            <MenuItem key={quality.Key} value={quality.Key}>{quality.Name}</MenuItem>
                                        ))}
                                    </Select>
                                    {isRatedQuality(qualities, mod.Key) &&
                                        <Select
                                            value={qualityModeOf(mod)}
                                            onChange={(e) => {
                                                const { QualSet, QualReplace, NewCount, ...rest } = mod
                                                const modeFlags = { grant: { QualSet: "true" }, replace: { QualReplace: "true" }, new: { NewCount: NewCount || rest.Count || "1" } }
                                                update(index, { ...rest, ...(modeFlags[e.target.value] || {}) })
                                            }}
                                            sx={{ marginLeft: "4px" }}
                                        >
                                            <MenuItem value="add">Increase by (+N)</MenuItem>
                                            <MenuItem value="new">Gain, or increase if present</MenuItem>
                                            <MenuItem value="grant">Grant (keep higher)</MenuItem>
                                            <MenuItem value="replace">Set to (overwrite)</MenuItem>
                                        </Select>
                                    }
                                    {qualityModeOf(mod) === "new" &&
                                        <TextField
                                            label="Gain at"
                                            value={mod.NewCount || ""}
                                            onChange={(e) => update(index, { ...mod, NewCount: !isNaN(e.target.value) && e.target.value !== "" ? e.target.value : mod.NewCount || "1" })}
                                            sx={{ maxWidth: "90px", marginLeft: "4px" }}
                                        />
                                    }
                                    <TextField
                                        label={{ add: "Ranks", new: "Else +", grant: "Rating", replace: "Rating" }[qualityModeOf(mod)]}
                                        value={mod.Count || 0}
                                        onChange={(e) => update(index, { ...mod, Count: !isNaN(e.target.value) ? e.target.value : mod.Count || 0 })}
                                        sx={{ maxWidth: "90px" }}
                                        disabled={!!mod.WeaponCountDamageMult}
                                    />
                                    {isRatedQuality(qualities, mod.Key) &&
                                        <>
                                            <TextField
                                                label="Or × Damage"
                                                value={mod.WeaponCountDamageMult ?? ""}
                                                onChange={(e) => {
                                                    if (isNaN(e.target.value)) return
                                                    const { WeaponCountDamageMult, WeaponCountRound, ...rest } = mod
                                                    update(index, e.target.value === "" ? rest : { ...mod, WeaponCountDamageMult: e.target.value })
                                                }}
                                                sx={{ maxWidth: "110px", marginLeft: "4px" }}
                                            />
                                            {mod.WeaponCountDamageMult &&
                                                <Select
                                                    value={mod.WeaponCountRound === "up" ? "up" : "down"}
                                                    onChange={(e) => update(index, { ...mod, WeaponCountRound: e.target.value === "up" ? "up" : undefined })}
                                                    sx={{ marginLeft: "4px" }}
                                                >
                                                    <MenuItem value="down">Round down</MenuItem>
                                                    <MenuItem value="up">Round up</MenuItem>
                                                </Select>
                                            }
                                        </>
                                    }
                                    {mod.Key === "RANGESUB" &&
                                        <Select
                                            value={mod.MinRange || ""}
                                            displayEmpty
                                            onChange={(e) => update(index, { ...mod, MinRange: e.target.value || undefined })}
                                            sx={{ marginLeft: "4px", minWidth: "150px" }}
                                        >
                                            <MenuItem value="">No minimum</MenuItem>
                                            {RANGE_BANDS.map((band) => (
                                                <MenuItem key={band} value={band}>{`Minimum ${band}`}</MenuItem>
                                            ))}
                                        </Select>
                                    }
                                </>
                            }
                            {hasRangeReduction && mod.Key !== "RANGESUB" &&
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <span>Only if range reduction is blocked by its minimum:</span>
                                    <Checkbox
                                        checked={!!mod.IfRangeBlocked}
                                        onChange={(e) => update(index, { ...mod, IfRangeBlocked: e.target.checked ? "true" : undefined })}
                                    />
                                </Box>
                            }
                            {integratedTarget && !mod.DieModifiers &&
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <span>Applies to integrated weapon:</span>
                                    <Checkbox
                                        checked={!!mod.ToIntegrated}
                                        onChange={(e) => update(index, { ...mod, ToIntegrated: e.target.checked || undefined })}
                                    />
                                </Box>
                            }
                            <IconButton onClick={() => remove(index)} sx={{ maxWidth: "30px" }} size="small" className="no-print">
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    ))}
                </Grid>
            )}
            <Button onClick={() => onChange([...mods, { MiscDesc: "" }])} className="no-print">Add Custom Mod</Button>
            <Button onClick={() => onChange([...mods, { Key: "BR" }])} className="no-print">Add Characteristic Mod</Button>
            <Button onClick={() => onChange([...mods, { Key: "ASTRO" }])} className="no-print">Add Skill Mod</Button>
            <Button onClick={() => onChange([...mods, { DieModifiers: [{ SkillKey: sortedSkills[0]?.Key || "" }] }])} className="no-print">Add Roll Mod</Button>
            <Button onClick={() => onChange([...mods, { Key: "ADV" }])} className="no-print">Add Talent Mod</Button>
            {filteredQualities[0] &&
                <Button onClick={() => onChange([...mods, { Key: filteredQualities[0].Key }])} className="no-print">Add Quality</Button>
            }
            {integratedTarget && integratedQualities[0] &&
                <Button onClick={() => onChange([...mods, { Key: integratedQualities[0].Key, ToIntegrated: true }])} className="no-print">Add Integrated Weapon Mod</Button>
            }
            {adjustTargets[0] &&
                <Button onClick={() => onChange([...mods, { Key: "REMQUAL" + adjustTargets[0].Key, Count: "1" }])} className="no-print">Add Remove/Reduce Quality</Button>
            }
        </>
    );
};

const VEHICLE_STATS = ["HP", "Handling", "Armor", "SystemStrain", "HullTrauma", "Speed", "Silhouette", "DefFore", "DefAft", "DefPort", "DefStarboard"];

export const VehicleChangesEditor = ({ value = {}, onChange, qualities = [] }) => {
    const stats = Array.isArray(value.Stats) ? value.Stats : [];
    const weaponQualities = Array.isArray(value.WeaponQualities) ? value.WeaponQualities : [];
    const commit = (patch) => {
        const next = { ...value, ...patch };
        Object.keys(next).forEach((k) => {
            const v = next[k];
            if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) { delete next[k]; }
        });
        onChange(Object.keys(next).length ? next : undefined);
    };

    const setStats = (newStats) => commit({ Stats: newStats });
    const updateStat = (i, patch) => setStats(stats.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
    const removeStat = (i) => setStats(stats.filter((_, idx) => idx !== i));

    const setWeaponQualities = (newWQ) => commit({ WeaponQualities: newWQ });
    const updateWQ = (i, patch) => setWeaponQualities(weaponQualities.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
    const removeWQ = (i) => setWeaponQualities(weaponQualities.filter((_, idx) => idx !== i));

    return (
        <>
            {stats.map((s, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px", marginBottom: "5px" }}>
                    <Select
                        value={s.Stat || ""}
                        displayEmpty
                        onChange={(e) => updateStat(i, { Stat: e.target.value })}
                        sx={{ minWidth: "160px" }}
                    >
                        <MenuItem value=""><em>Stat</em></MenuItem>
                        {VEHICLE_STATS.map((st) => (<MenuItem key={st} value={st}>{st}</MenuItem>))}
                    </Select>
                    <TextField
                        label="Amount +/-"
                        value={s.Amount ?? ""}
                        onChange={(e) => { if (!isNaN(e.target.value) || e.target.value === "-") { updateStat(i, { Amount: e.target.value }); } }}
                        sx={{ maxWidth: "110px" }}
                    />
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography>Per Rank</Typography>
                        <Checkbox
                            checked={s.PerRank === "true"}
                            onChange={(e) => updateStat(i, { PerRank: e.target.checked ? "true" : undefined })}
                        />
                    </Box>
                    <IconButton onClick={() => removeStat(i)} sx={{ maxWidth: "30px" }} size="small" className="no-print">
                        <CloseIcon />
                    </IconButton>
                </Box>
            ))}
            <Button onClick={() => setStats([...stats, { Stat: "", Amount: "1" }])} className="no-print">Add Vehicle Stat Change</Button>

            <Typography variant="subtitle2" sx={{ marginTop: "10px" }}>Vehicle Weapon Quality Changes:</Typography>
            {weaponQualities.map((wq, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px", marginBottom: "5px" }}>
                    <Select
                        value={wq.Key || ""}
                        displayEmpty
                        onChange={(e) => updateWQ(i, { Key: e.target.value })}
                        sx={{ minWidth: "170px" }}
                    >
                        <MenuItem value=""><em>Quality</em></MenuItem>
                        {weaponQualityOptions(qualities, wq.Key).map((q) => (<MenuItem key={q.Key} value={q.Key}>{q.Name}</MenuItem>))}
                    </Select>
                    <TextField
                        label="Amount +/-"
                        value={wq.Amount ?? ""}
                        onChange={(e) => { if (!isNaN(e.target.value) || e.target.value === "-") { updateWQ(i, { Amount: e.target.value }); } }}
                        sx={{ maxWidth: "110px" }}
                    />
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography>Per Rank</Typography>
                        <Checkbox
                            checked={wq.PerRank === "true"}
                            onChange={(e) => updateWQ(i, { PerRank: e.target.checked ? "true" : undefined })}
                        />
                    </Box>
                    <IconButton onClick={() => removeWQ(i)} sx={{ maxWidth: "30px" }} size="small" className="no-print">
                        <CloseIcon />
                    </IconButton>
                </Box>
            ))}
            <Button onClick={() => setWeaponQualities([...weaponQualities, { Key: "", Amount: "1" }])} className="no-print">Add Weapon Quality Change</Button>

            <Box sx={{ display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap", marginTop: "10px" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography>Makes Massive</Typography>
                    <Checkbox
                        checked={value.Massive === "true"}
                        onChange={(e) => commit({ Massive: e.target.checked ? "true" : undefined })}
                    />
                </Box>
                <TextField
                    label="Increase Max Silhouette (per rank)"
                    value={value.ProjectSize || ""}
                    onChange={(e) => { if (!isNaN(e.target.value)) { commit({ ProjectSize: e.target.value || undefined }); } }}
                    sx={{ maxWidth: "230px" }}
                />
                <TextField
                    label="Signature Vehicle Note (added as a mod)"
                    value={value.BaseMod || ""}
                    onChange={(e) => commit({ BaseMod: e.target.value || undefined })}
                    sx={{ flex: 1, minWidth: "260px" }}
                />
            </Box>
        </>
    );
};

export const ITEM_MOD_OPTIONS = [
    { key: "DAMADD", label: "+1 Damage" },
    { key: "DAMSUB", label: "-1 Damage" },
    { key: "CRITSUB", label: "-1 Crit" },
    { key: "ENCSUB", label: "-1 Encumbrance" },
    { key: "ENCSUB2", label: "-2 Encumbrance" },
    { key: "HPADD", label: "+1 Hard Point" },
    { key: "SOAKADD", label: "+1 Soak" },
    { key: "DEFADD", label: "+1 Defense" },
    { key: "MELEEDEFADD", label: "+1 Melee Defense" },
    { key: "RANGEDEFADD", label: "+1 Ranged Defense" },
    { key: "RANGEADD", label: "+1 Range" },
    { key: "RANGESUB", label: "-1 Range" },
];

const CHOICE_TYPES = ["Skill", "Characteristic", "Weapon", "Item", "ItemMod", "Vehicle"];
const CHOICE_TYPE_LABELS = { Skill: "Pick Skill", Characteristic: "Pick Characteristic", Weapon: "Pick Weapon", Item: "Pick Item", ItemMod: "Pick Item + Mod", Vehicle: "Pick Vehicle" };

export const ChoicesEditor = ({ choices = [], onChange, skills }) => {
    const sortedSkills = [...skills].sort((a, b) => (a.skill || "").localeCompare(b.skill || ""));
    const skillTypes = [...new Set(skills.map((s) => s.type).filter(Boolean))];

    const updateChoice = (i, patch) => {
        const next = choices.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
        onChange(next);
    };
    const removeChoice = (i) => onChange(choices.filter((_, idx) => idx !== i));

    const setFlag = (i, key, checked) => {
        const c = { ...choices[i] };
        if (checked) { c[key] = "true"; } else { delete c[key]; }
        updateChoice(i, c);
    };

    return (
        <>
            {choices.map((choice, i) => (
                <Paper key={i} sx={{ marginTop: "10px", padding: "10px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                        <Select
                            value={choice.Type || "Skill"}
                            onChange={(e) => updateChoice(i, { Type: e.target.value })}
                            sx={{ minWidth: "150px" }}
                        >
                            {CHOICE_TYPES.map((t) => (<MenuItem key={t} value={t}>{CHOICE_TYPE_LABELS[t] || `Pick ${t}`}</MenuItem>))}
                        </Select>
                        <TextField
                            label="Prompt"
                            value={choice.Prompt || ""}
                            onChange={(e) => updateChoice(i, { Prompt: e.target.value })}
                            sx={{ minWidth: "220px" }}
                        />
                        <TextField
                            label="How Many"
                            value={choice.Count || "1"}
                            onChange={(e) => { if (!isNaN(e.target.value)) { updateChoice(i, { Count: e.target.value }); } }}
                            sx={{ maxWidth: "100px" }}
                        />
                        <IconButton onClick={() => removeChoice(i)} sx={{ maxWidth: "30px" }} size="small" className="no-print">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "15px", marginTop: "10px" }}>
                        {choice.Type === "Skill" && (
                            <>
                                <Select
                                    value={choice.SkillType || "Any"}
                                    onChange={(e) => updateChoice(i, { SkillType: e.target.value })}
                                    sx={{ minWidth: "150px" }}
                                >
                                    <MenuItem value="Any">Any Skill</MenuItem>
                                    {skillTypes.map((tp) => (<MenuItem key={tp} value={tp}>{`${tp} Only`}</MenuItem>))}
                                </Select>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Typography>Make Career</Typography>
                                    <Checkbox checked={choice.MakeCareer === "true"} onChange={(e) => setFlag(i, "MakeCareer", e.target.checked)} />
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Typography>Boost Die</Typography>
                                    <Checkbox checked={choice.BoostDie === "true"} onChange={(e) => setFlag(i, "BoostDie", e.target.checked)} />
                                </Box>
                                <Select
                                    multiple
                                    displayEmpty
                                    value={choice.SkillKeys || []}
                                    onChange={(e) => {
                                        const picked = e.target.value.filter(Boolean);
                                        updateChoice(i, { SkillKeys: picked.length ? picked : undefined });
                                    }}
                                    renderValue={(sel) => (sel.length === 0 ? "Limit to specific skills (optional)" : sel.map((k) => skills.find((s) => s.Key === k)?.skill || k).join(", "))}
                                    sx={{ minWidth: "260px" }}
                                >
                                    {sortedSkills.map((s) => (<MenuItem key={s.Key} value={s.Key}>{s.skill}</MenuItem>))}
                                </Select>
                            </>
                        )}
                        {choice.Type === "Weapon" && (
                            <>
                                <Select
                                    value={choice.WeaponSkill || ""}
                                    displayEmpty
                                    onChange={(e) => updateChoice(i, { WeaponSkill: e.target.value || undefined })}
                                    sx={{ minWidth: "170px" }}
                                >
                                    <MenuItem value=""><em>Any Weapon Skill</em></MenuItem>
                                    {sortedSkills.map((s) => (<MenuItem key={s.Key} value={s.Key}>{s.skill}</MenuItem>))}
                                </Select>
                                <TextField
                                    label="Set Property (for Weapon Buffs)"
                                    value={choice.SetProperty || ""}
                                    onChange={(e) => updateChoice(i, { SetProperty: e.target.value || undefined })}
                                    sx={{ minWidth: "240px" }}
                                />
                            </>
                        )}
                        {choice.Type === "Item" && (
                            <>
                                <Select
                                    value={choice.ItemCategory || "All"}
                                    onChange={(e) => updateChoice(i, { ItemCategory: e.target.value })}
                                    sx={{ minWidth: "150px" }}
                                >
                                    {["All", "Weapon", "Armor", "Gear"].map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
                                </Select>
                                <TextField
                                    label="Set Property"
                                    value={choice.SetProperty || ""}
                                    onChange={(e) => updateChoice(i, { SetProperty: e.target.value || undefined })}
                                    sx={{ minWidth: "200px" }}
                                />
                                <TextField
                                    label="Hard Points +/-"
                                    value={choice.HPChange || ""}
                                    onChange={(e) => { if (!isNaN(e.target.value) || e.target.value === "-") { updateChoice(i, { HPChange: e.target.value || undefined }); } }}
                                    sx={{ maxWidth: "130px" }}
                                />
                            </>
                        )}
                        {choice.Type === "ItemMod" && (
                            <>
                                <Select
                                    value={choice.ItemCategory || "All"}
                                    onChange={(e) => updateChoice(i, { ItemCategory: e.target.value })}
                                    sx={{ minWidth: "150px" }}
                                >
                                    {["All", "Weapon", "Armor", "Gear"].map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
                                </Select>
                                <Select
                                    multiple
                                    displayEmpty
                                    value={choice.ModKeys || []}
                                    onChange={(e) => updateChoice(i, { ModKeys: e.target.value.filter(Boolean) })}
                                    renderValue={(sel) => (sel.length === 0 ? "Allowed modifications" : sel.map((k) => (ITEM_MOD_OPTIONS.find((o) => o.key === k)?.label) || k).join(", "))}
                                    sx={{ minWidth: "280px" }}
                                >
                                    {ITEM_MOD_OPTIONS.map((o) => (<MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>))}
                                </Select>
                                <Box sx={{ width: "100%" }}>
                                    <Typography variant="caption">Text only options (added to the item as a note):</Typography>
                                    {(choice.CustomOptions || []).map((opt, oi) => (
                                        <Box key={oi} sx={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "3px" }}>
                                            <TextField
                                                value={opt}
                                                onChange={(e) => {
                                                    const next = [...(choice.CustomOptions || [])];
                                                    next[oi] = e.target.value;
                                                    updateChoice(i, { CustomOptions: next });
                                                }}
                                                sx={{ minWidth: "340px" }}
                                            />
                                            <IconButton
                                                onClick={() => {
                                                    const next = (choice.CustomOptions || []).filter((_, idx) => idx !== oi);
                                                    updateChoice(i, { CustomOptions: next.length ? next : undefined });
                                                }}
                                                size="small" className="no-print"
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        onClick={() => updateChoice(i, { CustomOptions: [...(choice.CustomOptions || []), ""] })}
                                        className="no-print"
                                    >
                                        Add No-Mechanics Option
                                    </Button>
                                </Box>
                            </>
                        )}
                        {choice.Type === "Characteristic" && (
                            <Typography sx={{ color: "text.secondary" }}>Raises the chosen characteristic by 1 (Dedication-style).</Typography>
                        )}
                        {choice.Type === "Vehicle" && (
                            <Typography sx={{ color: "text.secondary" }}>Designates the chosen vehicle as a Signature Vehicle.</Typography>
                        )}
                    </Box>
                </Paper>
            ))}
            <Button onClick={() => onChange([...choices, { Type: "Skill", Count: "1" }])} className="no-print">Add Player Choice</Button>
        </>
    );
};

export const SkillCharSwapEditor = ({ value, skills, onChange }) => {
    const sortedSkills = [...skills].sort((a, b) => (a.skill || "").localeCompare(b.skill || ""));
    const rows = value ? (Array.isArray(value) ? value : [value]) : [];
    const editRows = rows.map((r) => ({
        SkillKey: r.SkillKey || '',
        CharKeys: r.CharKeys ? [...r.CharKeys] : (r.CharKey ? [r.CharKey] : []),
    }));

    const charName = (k) => charactersticsData.find((c) => c.Key === k)?.Name || k;

    const commit = (newRows) => {
        const cleaned = newRows
            .filter((r) => r.SkillKey && r.CharKeys.length)
            .map((r) => (r.CharKeys.length === 1
                ? { SkillKey: r.SkillKey, CharKey: r.CharKeys[0] }
                : { SkillKey: r.SkillKey, CharKeys: r.CharKeys }));
        if (cleaned.length === 0) { onChange(undefined); }
        else { onChange(cleaned.length === 1 ? cleaned[0] : cleaned); }
    };

    const updateRow = (i, patch) => {
        commit(editRows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    };

    return (
        <>
            {editRows.map((row, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px", marginBottom: "5px" }}>
                    <Select
                        value={row.SkillKey}
                        displayEmpty
                        onChange={(e) => updateRow(i, { SkillKey: e.target.value })}
                        sx={{ minWidth: "180px" }}
                    >
                        <MenuItem value=""><em>Skill</em></MenuItem>
                        {sortedSkills.map((s) => (
                            <MenuItem key={s.Key} value={s.Key}>{s.skill}</MenuItem>
                        ))}
                    </Select>
                    <Typography>may use</Typography>
                    <Select
                        multiple
                        value={row.CharKeys}
                        displayEmpty
                        onChange={(e) => updateRow(i, { CharKeys: e.target.value.filter(Boolean) })}
                        renderValue={(sel) => (sel.length === 0 ? "Characteristic" : sel.map(charName).join(", "))}
                        sx={{ minWidth: "220px" }}
                    >
                        {charactersticsData.map((c) => (
                            <MenuItem key={c.Key} value={c.Key}>{c.Name}</MenuItem>
                        ))}
                    </Select>
                    <Typography>instead</Typography>
                    <IconButton
                        onClick={() => commit(editRows.filter((_, idx) => idx !== i))}
                        sx={{ maxWidth: "30px" }}
                        size="small"
                        className="no-print"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            ))}
            <Button
                onClick={() => commit([...editRows, { SkillKey: sortedSkills[0]?.Key || '', CharKeys: [charactersticsData[0].Key] }])}
                className="no-print"
            >
                Add Characteristic Swap
            </Button>
        </>
    );
};

export const ItemChangesEditor = ({ itemChanges = [], onChange, skills, qualities }) => {
    const sortedSkills = [...skills].sort((a, b) => (a.skill || "").localeCompare(b.skill || ""));
    const updateChange = (index, newChange) => {
        const next = [...itemChanges];
        next[index] = newChange;
        onChange(next);
    };
    const removeChange = (index) => onChange(itemChanges.filter((_, i) => i !== index));

    const numField = (label, value, onValue, width = "110px") => (
        <TextField
            label={label}
            value={value ?? ""}
            onChange={(e) => { if (!isNaN(e.target.value) || e.target.value === "-") { onValue(e.target.value); } }}
            sx={{ maxWidth: width }}
        />
    );

    return (
        <>
            {itemChanges.map((change, index) => {
                const qualityChanges = Array.isArray(change.QualityChanges)
                    ? change.QualityChanges
                    : (change.QualityChanges ? [change.QualityChanges] : []);

                const updateQuality = (qI, newQ) => {
                    const next = [...qualityChanges];
                    next[qI] = newQ;
                    updateChange(index, { ...change, QualityChanges: next });
                };

                const setField = (key, value) => {
                    const next = { ...change };
                    if (value === undefined || value === "" || value === "0" || value === "false") { delete next[key]; }
                    else { next[key] = value; }
                    updateChange(index, next);
                };

                return (
                    <Paper key={index} sx={{ marginTop: "10px", padding: "10px" }}>
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                            <Typography sx={{ fontWeight: "bold" }}>Applies to weapons:</Typography>
                            <Select
                                value={change.SkillKey || ''}
                                displayEmpty
                                onChange={(e) => setField('SkillKey', e.target.value)}
                                sx={{ minWidth: "170px" }}
                            >
                                <MenuItem value=""><em>Any Skill</em></MenuItem>
                                {sortedSkills.map((skill) => (
                                    <MenuItem key={skill.Key} value={skill.Key}>{skill.skill}</MenuItem>
                                ))}
                            </Select>
                            <TextField
                                label="Specific Item Key"
                                value={change.ItemKey || ''}
                                onChange={(e) => setField('ItemKey', e.target.value)}
                                sx={{ maxWidth: "160px" }}
                            />
                            <TextField
                                label="Has Property"
                                value={change.ItemProperty || ''}
                                onChange={(e) => setField('ItemProperty', e.target.value)}
                                sx={{ maxWidth: "160px" }}
                            />
                            <IconButton onClick={() => removeChange(index)} sx={{ maxWidth: "30px" }} size="small" className="no-print">
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "15px", marginTop: "10px" }}>
                            {numField("Damage +/-", change.DamageChange, (v) => setField('DamageChange', v))}
                            {numField("Crit +/-", change.CritChange, (v) => setField('CritChange', v))}
                            {numField("Hard Points +/-", change.HPChange, (v) => setField('HPChange', v))}
                            {numField("Encumbrance +/-", change.EncumbranceChange, (v) => setField('EncumbranceChange', v), "130px")}
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography>Grant quality if weapon lacks it</Typography>
                                <Checkbox
                                    checked={change.AddQuality === "true"}
                                    onChange={(e) => setField('AddQuality', e.target.checked ? "true" : "false")}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ marginTop: "5px" }}>
                            {qualityChanges.map((q, qI) => {
                                const ratingSource = q.UseForceRating === "true" ? "force" : (q.UseSkillRanks ? "skill" : "fixed");
                                const setRatingSource = (src) => {
                                    const nq = { ...q };
                                    delete nq.UseForceRating; delete nq.UseSkillRanks; delete nq.Count;
                                    if (src === "force") { nq.UseForceRating = "true"; }
                                    else if (src === "skill") { nq.UseSkillRanks = sortedSkills[0]?.Key || ""; }
                                    updateQuality(qI, nq);
                                };
                                return (
                                    <Box key={qI} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", marginBottom: "5px" }}>
                                        <Select
                                            value={q.Key || ''}
                                            displayEmpty
                                            onChange={(e) => updateQuality(qI, { ...q, Key: e.target.value })}
                                            sx={{ minWidth: "160px" }}
                                        >
                                            <MenuItem value=""><em>Quality</em></MenuItem>
                                            {weaponQualityOptions(qualities, q.Key).map((ql) => (
                                                <MenuItem key={ql.Key} value={ql.Key}>{ql.Name}</MenuItem>
                                            ))}
                                        </Select>
                                        <Select
                                            value={ratingSource}
                                            onChange={(e) => setRatingSource(e.target.value)}
                                            sx={{ minWidth: "150px", marginLeft: 1 }}
                                        >
                                            <MenuItem value="fixed">Fixed Rating</MenuItem>
                                            <MenuItem value="force">= Force Rating</MenuItem>
                                            <MenuItem value="skill">= Skill Ranks</MenuItem>
                                        </Select>
                                        {ratingSource === "fixed" && (
                                            <TextField
                                                label="Rating"
                                                value={q.Count ?? ""}
                                                onChange={(e) => { if (!isNaN(e.target.value)) { updateQuality(qI, { ...q, Count: e.target.value }); } }}
                                                sx={{ maxWidth: "90px", marginLeft: 1 }}
                                            />
                                        )}
                                        {ratingSource === "skill" && (
                                            <Select
                                                value={q.UseSkillRanks || ''}
                                                displayEmpty
                                                onChange={(e) => updateQuality(qI, { ...q, UseSkillRanks: e.target.value })}
                                                sx={{ minWidth: "160px", marginLeft: 1 }}
                                            >
                                                <MenuItem value=""><em>Skill</em></MenuItem>
                                                {sortedSkills.map((skill) => (
                                                    <MenuItem key={skill.Key} value={skill.Key}>{skill.skill}</MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                        <IconButton
                                            onClick={() => updateChange(index, { ...change, QualityChanges: qualityChanges.filter((_, i) => i !== qI) })}
                                            sx={{ maxWidth: "30px" }}
                                            size="small"
                                            className="no-print"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>
                                );
                            })}
                            <Button
                                onClick={() => updateChange(index, { ...change, QualityChanges: [...qualityChanges, { Key: '' }] })}
                                className="no-print"
                            >
                                Add Quality Change
                            </Button>
                        </Box>
                    </Paper>
                );
            })}
            <Button onClick={() => onChange([...itemChanges, {}])} className="no-print">Add Weapon/Attack Buff</Button>
        </>
    );
};

const normalizeWeaponModifiers = (value) => {
    if (!value) { return []; }
    const list = Array.isArray(value) ? value : value.WeaponModifier ? [].concat(value.WeaponModifier) : [value];
    return list.filter(Boolean).map((w) => {
        const { UnarmedName, ...rest } = w;
        const q = rest.Qualities;
        const qualities = !q ? q : Array.isArray(q) ? q : q.Quality ? [].concat(q.Quality) : [q];
        return { ...rest, Name: rest.Name ?? UnarmedName, Qualities: qualities };
    });
};

const toggleBrawn = (weapon, checked) => {
    const { Damage, DamageAdd, ...rest } = weapon;
    const carried = (DamageAdd !== undefined ? DamageAdd : Damage) ?? "";
    return checked ? { ...rest, DamageAdd: carried } : { ...rest, Damage: carried };
};

export const IntegratedWeaponsEditor = ({ weaponModifiers: rawWeaponModifiers, onChange, skills, qualities }) => {
    const weaponModifiers = normalizeWeaponModifiers(rawWeaponModifiers);
    const updateWeapon = (index, newWeapon) => {
        const next = [...weaponModifiers];
        next[index] = newWeapon;
        onChange(next);
    };

    return (
        <>
            <Typography sx={{ marginTop: "10px" }}>Integrated Weapons:</Typography>
            {weaponModifiers.map((weapon, index) => (
                <Paper key={index} sx={{ marginTop: "10px", padding: "5px" }}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item>
                            <IconButton
                                onClick={() => onChange(weaponModifiers.filter((_, i) => i !== index))}
                                sx={{ maxWidth: "30px" }}
                                size="small"
                                className="no-print"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                        <Grid item>
                            <TextField
                                label="Name"
                                value={weapon.Name || ''}
                                onChange={(e) => updateWeapon(index, { ...weapon, Name: e.target.value })}
                            />
                        </Grid>
                        <Grid item>
                            <Select
                                value={weapon.SkillKey || ''}
                                displayEmpty
                                onChange={(e) => updateWeapon(index, { ...weapon, SkillKey: e.target.value })}
                                sx={{ minWidth: "150px" }}
                            >
                                <MenuItem value=""><em>Skill</em></MenuItem>
                                {[...skills].sort((a, b) => (a.skill || "").localeCompare(b.skill || "")).map((skill) => (
                                    <MenuItem key={skill.Key} value={skill.Key}>{skill.skill}</MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        <Grid item>
                            <TextField
                                label={weapon.DamageAdd !== undefined ? "Damage (Brawn +)" : "Damage"}
                                value={(weapon.DamageAdd !== undefined ? weapon.DamageAdd : weapon.Damage) ?? ''}
                                sx={{ maxWidth: "130px" }}
                                onChange={(e) => updateWeapon(index, weapon.DamageAdd !== undefined
                                    ? { ...weapon, DamageAdd: e.target.value }
                                    : { ...weapon, Damage: e.target.value })} />
                        </Grid>
                        <Grid item sx={{ display: "flex", alignItems: "center" }}>
                            <span>+Brawn:</span>
                            <Checkbox
                                checked={weapon.DamageAdd !== undefined}
                                onChange={(e) => updateWeapon(index, toggleBrawn(weapon, e.target.checked))}
                            />
                        </Grid>
                        <Grid item>
                            <TextField label="Crit" value={weapon.Crit || ''} sx={{ maxWidth: "90px" }}
                                onChange={(e) => updateWeapon(index, { ...weapon, Crit: e.target.value })} />
                        </Grid>
                        <Grid item>
                            <TextField label="Range" value={weapon.Range || ''} sx={{ maxWidth: "120px" }}
                                onChange={(e) => updateWeapon(index, { ...weapon, Range: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                            <Typography sx={{ marginRight: 1 }}>Qualities:</Typography>
                            {(weapon.Qualities || []).map((quality, qI) => (
                                <Box key={qI} sx={{ display: "flex", alignItems: "center" }}>
                                    <Select
                                        value={quality.Key || ''}
                                        displayEmpty
                                        onChange={(e) => {
                                            const newQualities = [...(weapon.Qualities || [])];
                                            newQualities[qI] = { ...newQualities[qI], Key: e.target.value };
                                            updateWeapon(index, { ...weapon, Qualities: newQualities });
                                        }}
                                        sx={{ minWidth: "140px" }}
                                    >
                                        <MenuItem value=""><em>Quality</em></MenuItem>
                                        {weaponQualityOptions(qualities, quality.Key).map((q) => (
                                            <MenuItem key={q.Key} value={q.Key}>{q.Name}</MenuItem>
                                        ))}
                                    </Select>
                                    <TextField
                                        label="Rating"
                                        value={quality.Count || ''}
                                        onChange={(e) => {
                                            const newQualities = [...(weapon.Qualities || [])];
                                            newQualities[qI] = { ...newQualities[qI], Count: e.target.value };
                                            updateWeapon(index, { ...weapon, Qualities: newQualities });
                                        }}
                                        sx={{ maxWidth: "80px" }}
                                    />
                                    <IconButton
                                        onClick={() => updateWeapon(index, { ...weapon, Qualities: (weapon.Qualities || []).filter((_, i) => i !== qI) })}
                                        sx={{ maxWidth: "30px" }}
                                        size="small"
                                        className="no-print"
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button
                                onClick={() => updateWeapon(index, { ...weapon, Qualities: [...(weapon.Qualities || []), {}] })}
                                className="no-print"
                            >
                                Add Quality
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            ))}
            <Button
                onClick={() => onChange([...weaponModifiers, { Name: "", Qualities: [] }])}
                className="no-print"
            >
                Add Integrated Weapon
            </Button>
        </>
    );
};

export const SpeciesWeaponEditor = ({ value, onChange, skills, qualities }) => {
    if (!value) {
        return (
            <>
                <Typography sx={{ marginTop: "10px" }}>Natural Weapon:</Typography>
                <Button
                    onClick={() => onChange({ Mode: "AllBrawl", UnarmedName: "", DamageAdd: "0", Crit: "", Qualities: [] })}
                    className="no-print"
                >
                    Add Natural Weapon
                </Button>
            </>
        );
    }
    const mode = speciesWeaponMode(value);
    const set = (patch) => {
        const { Secondary, AllSkillKey, ...rest } = value;
        onChange({ ...rest, Mode: mode, ...patch });
    };
    const qualitiesList = value.Qualities || [];

    return (
        <>
            <Typography sx={{ marginTop: "10px" }}>Natural Weapon:</Typography>
            <Paper sx={{ marginTop: "5px", padding: "5px" }}>
                <Grid container spacing={1} alignItems="center">
                    <Grid item>
                        <IconButton onClick={() => onChange(undefined)} sx={{ maxWidth: "30px" }} size="small" className="no-print">
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                    <Grid item>
                        <Select value={mode} onChange={(e) => set({ Mode: e.target.value })} sx={{ minWidth: "220px" }}>
                            {SPECIES_WEAPON_MODES.map((m) => (<MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>))}
                        </Select>
                    </Grid>
                    <Grid item>
                        <TextField label="Name" value={value.UnarmedName || ''}
                            onChange={(e) => set({ UnarmedName: e.target.value })} />
                    </Grid>
                    {mode === "Secondary" && (
                        <>
                            <Grid item>
                                <Select value={value.SkillKey || 'BRAWL'} onChange={(e) => set({ SkillKey: e.target.value })} sx={{ minWidth: "150px" }}>
                                    {[...skills].sort((a, b) => (a.skill || "").localeCompare(b.skill || "")).map((skill) => (
                                        <MenuItem key={skill.Key} value={skill.Key}>{skill.skill}</MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            <Grid item>
                                <Select value={value.Characteristic || ''} displayEmpty
                                    onChange={(e) => set({ Characteristic: e.target.value || undefined })} sx={{ minWidth: "150px" }}>
                                    <MenuItem value=""><em>Skill Default</em></MenuItem>
                                    {charactersticsData.map((c) => (<MenuItem key={c.Key} value={c.Name}>{c.Name}</MenuItem>))}
                                </Select>
                            </Grid>
                            <Grid item>
                                <TextField label="Damage" value={value.Damage || ''} sx={{ maxWidth: "90px" }}
                                    onChange={(e) => set({ Damage: e.target.value || undefined })} />
                            </Grid>
                        </>
                    )}
                    <Grid item>
                        <TextField label="Damage +" value={value.DamageAdd || ''} sx={{ maxWidth: "90px" }}
                            onChange={(e) => set({ DamageAdd: e.target.value })} />
                    </Grid>
                    <Grid item>
                        <TextField label="Crit" value={value.Crit || ''} sx={{ maxWidth: "90px" }}
                            onChange={(e) => set({ Crit: e.target.value })} />
                    </Grid>
                    {mode === "Secondary" && (
                        <Grid item>
                            <TextField label="Range" value={value.Range || ''} sx={{ maxWidth: "120px" }}
                                onChange={(e) => set({ Range: e.target.value })} />
                        </Grid>
                    )}
                    <Grid item xs={12} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                        <Typography sx={{ marginRight: 1 }}>Qualities:</Typography>
                        {qualitiesList.map((quality, qI) => (
                            <Box key={qI} sx={{ display: "flex", alignItems: "center" }}>
                                <Select value={quality.Key || ''} displayEmpty sx={{ minWidth: "140px" }}
                                    onChange={(e) => {
                                        const next = [...qualitiesList];
                                        next[qI] = { ...next[qI], Key: e.target.value };
                                        set({ Qualities: next });
                                    }}>
                                    <MenuItem value=""><em>Quality</em></MenuItem>
                                    {weaponQualityOptions(qualities, quality.Key).map((q) => (
                                        <MenuItem key={q.Key} value={q.Key}>{q.Name}</MenuItem>
                                    ))}
                                </Select>
                                <TextField label="Rating" value={quality.Count || ''} sx={{ maxWidth: "80px" }}
                                    onChange={(e) => {
                                        const next = [...qualitiesList];
                                        next[qI] = { ...next[qI], Count: e.target.value || undefined };
                                        set({ Qualities: next });
                                    }} />
                                <IconButton onClick={() => set({ Qualities: qualitiesList.filter((_, i) => i !== qI) })}
                                    sx={{ maxWidth: "30px" }} size="small" className="no-print">
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        ))}
                        <Button onClick={() => set({ Qualities: [...qualitiesList, {}] })} className="no-print">
                            Add Quality
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
        </>
    );
};

export const BuiltInAttachmentsField = ({ value, onChange, attachments, type }) => {
    const relevant = type
        ? attachments.filter((a) => a.Type === type)
        : attachments;
    const list = relevant.length ? relevant : attachments;

    let selected = [];
    if (value && value.Key) { selected = Array.isArray(value.Key) ? value.Key : [value.Key]; }

    const orphans = selected.filter((k) => !list.some((a) => a.Key === k));
    const options = [
        ...[...list].sort((a, b) => a.Name.localeCompare(b.Name)),
        ...orphans.map((k) => ({ Key: k, Name: `${k} (unknown attachment)` })),
    ];

    const handleChange = (e) => {
        const picked = e.target.value.filter(Boolean);
        if (picked.length === 0) { onChange(undefined); }
        else { onChange({ Key: picked.length === 1 ? picked[0] : picked }); }
    };

    return (
        <Box sx={{ marginTop: "10px" }}>
            <Typography>Built-In Attachments (pre-equipped):</Typography>
            <Select
                multiple
                value={selected}
                onChange={handleChange}
                displayEmpty
                renderValue={(sel) =>
                    sel.length === 0
                        ? "None"
                        : sel.map((k) => options.find((a) => a.Key === k)?.Name || k).join(", ")
                }
                sx={{ minWidth: "300px", maxWidth: "100%" }}
            >
                {options.map((a) => (
                    <MenuItem key={a.Key} value={a.Key}>{a.Name}</MenuItem>
                ))}
            </Select>
        </Box>
    );
};

export const OrdnanceField = ({ value, onChange, weapons }) => {
    const selected = Array.isArray(value) ? value : value ? [value] : [];
    const list = weapons.filter((w) => w.Ordnance === true || w.Ordnance === "true");
    const orphans = selected.filter((k) => !list.some((w) => w.Key === k));
    const options = [
        ...[...list].sort((a, b) => a.Name.localeCompare(b.Name)),
        ...orphans.map((k) => ({ Key: k, Name: `${k} (unknown ordnance)` })),
    ];

    return (
        <Box sx={{ marginTop: "10px" }}>
            <Typography>Loadable Ordnance:</Typography>
            <Select
                multiple
                value={selected}
                onChange={(e) => {
                    const picked = e.target.value.filter(Boolean);
                    onChange(picked.length ? picked : undefined);
                }}
                displayEmpty
                renderValue={(sel) =>
                    sel.length === 0
                        ? "None"
                        : sel.map((k) => options.find((w) => w.Key === k)?.Name || k).join(", ")
                }
                sx={{ minWidth: "300px", maxWidth: "100%" }}
            >
                {options.map((w) => (
                    <MenuItem key={w.Key} value={w.Key}>{w.Name}</MenuItem>
                ))}
            </Select>
        </Box>
    );
};
